import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  Collapse,
  Stack,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccountBalance as DepositIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Home as RoomIcon,
  VpnKey as PasswordIcon,
  ContentCopy as CopyIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Assessment as ReportIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import tenantService from '../services/tenantService';
import invoiceService from '../services/invoiceService';
import paymentService from '../services/paymentService';
import reportService from '../services/reportService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PageHeader, Loading, EmptyState } from '../components/Layout';

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [openTempPasswordDialog, setOpenTempPasswordDialog] = useState(false);
  const [tempPasswordInfo, setTempPasswordInfo] = useState(null);
  const [selectedTenantForReset, setSelectedTenantForReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [editingTenant, setEditingTenant] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedTenantForPayment, setSelectedTenantForPayment] = useState(null);
  const [tenantInvoices, setTenantInvoices] = useState([]);
  const [paymentFormData, setPaymentFormData] = useState({
    invoice_id: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    transaction_ref: '',
    notes: '',
  });
  // Report generation state
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [selectedTenantForReport, setSelectedTenantForReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    period: 'monthly',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    start_date: '',
    end_date: '',
  });

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    national_id: '',
    opening_balance: '0',
    balance_type: 'credit', // credit (advance payment) or debit (arrears)
    balance_notes: '',
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await tenantService.getAllTenants();
      setTenants(response.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      // If it's a 404 or network error, just show empty state
      if (error.response?.status === 404 || error.code === 'ERR_BAD_REQUEST') {
        setTenants([]);
      } else {
        toast.error('Failed to load tenants');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (tenant = null) => {
    if (tenant) {
      setEditingTenant(tenant);
      setFormData({
        full_name: tenant.full_name,
        email: tenant.email,
        phone: tenant.phone,
        national_id: tenant.national_id,
        opening_balance: '0',
        balance_type: 'credit',
        balance_notes: '',
      });
    } else {
      setEditingTenant(null);
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        national_id: '',
        opening_balance: '0',
        balance_type: 'credit',
        balance_notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTenant(null);
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      national_id: '',
      opening_balance: '0',
      balance_type: 'credit',
      balance_notes: '',
    });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingTenant) {
        await tenantService.updateTenant(editingTenant.id, formData);
        toast.success('Tenant updated successfully');
        handleCloseDialog();
        fetchTenants();
      } else {
        const response = await tenantService.createTenant(formData);
        const data = response.data;
        
        handleCloseDialog();
        fetchTenants();
        
        // Show temporary password dialog
        if (data.temp_password) {
          setTempPasswordInfo({
            password: data.temp_password,
            name: data.full_name,
            email: data.email,
            username: data.email.split('@')[0]
          });
          setOpenTempPasswordDialog(true);
          toast.success('Tenant account created successfully');
        } else {
          toast.success('Tenant created successfully');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this tenant?')) {
      try {
        await tenantService.deleteTenant(id);
        toast.success('Tenant deleted successfully');
        fetchTenants();
      } catch (error) {
        toast.error('Failed to delete tenant');
        console.error(error);
      }
    }
  };

  const toggleExpandRow = (tenantId) => {
    setExpandedRows(prev => ({
      ...prev,
      [tenantId]: !prev[tenantId]
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleOpenPaymentDialog = async (tenant) => {
    setSelectedTenantForPayment(tenant);
    try {
      // Fetch tenant's unpaid/partial paid invoices
      const response = await invoiceService.getTenantInvoices(tenant.id);
      const invoices = response.data || [];
      
      console.log('All invoices fetched:', invoices);
      
      // Filter for invoices with outstanding balance, exclude voided/paid
      const unpaidInvoices = invoices.filter(inv => {
        if (inv.status === 'voided' || inv.status === 'paid') return false;
        const balance = inv.remaining_balance !== undefined 
          ? inv.remaining_balance 
          : (inv.total_with_penalty || inv.amount) - (inv.amount_paid || 0);
        return balance > 0.01;
      });
      
      console.log('Filtered unpaid invoices:', unpaidInvoices);
      setTenantInvoices(unpaidInvoices);
      
      // If there's only one unpaid invoice, auto-select it
      if (unpaidInvoices.length === 1) {
        const invoice = unpaidInvoices[0];
        const remainingBalance = invoice.remaining_balance !== undefined 
          ? invoice.remaining_balance 
          : (invoice.total_with_penalty || invoice.amount) - (invoice.amount_paid || 0);
        
        setPaymentFormData({
          invoice_id: invoice.id,
          amount: remainingBalance > 0 ? remainingBalance.toFixed(2) : '',
          payment_date: new Date().toISOString().split('T')[0],
          payment_method: 'cash',
          transaction_ref: '',
          notes: '',
        });
      } else {
        // Reset form for multiple invoices
        setPaymentFormData({
          invoice_id: '',
          amount: '',
          payment_date: new Date().toISOString().split('T')[0],
          payment_method: 'cash',
          transaction_ref: '',
          notes: '',
        });
      }
      
      setOpenPaymentDialog(true);
    } catch (error) {
      toast.error('Failed to load tenant invoices');
      console.error('Error loading invoices:', error);
    }
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setSelectedTenantForPayment(null);
    setTenantInvoices([]);
    setPaymentFormData({
      invoice_id: '',
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      transaction_ref: '',
      notes: '',
    });
  };

  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    
    // If invoice is selected, update the default amount to remaining balance
    if (name === 'invoice_id' && value) {
      const selectedInvoice = tenantInvoices.find(inv => inv.id === parseInt(value));
      if (selectedInvoice) {
        // Use backend-calculated remaining_balance if available
        const remainingBalance = selectedInvoice.remaining_balance !== undefined 
          ? selectedInvoice.remaining_balance 
          : (selectedInvoice.total_with_penalty || selectedInvoice.amount) - (selectedInvoice.amount_paid || 0);
        
        setPaymentFormData({
          ...paymentFormData,
          [name]: value,
          amount: remainingBalance > 0 ? remainingBalance.toFixed(2) : '',
        });
        return;
      }
    }
    
    setPaymentFormData({
      ...paymentFormData,
      [name]: value,
    });
  };

  const handleSubmitPayment = async () => {
    try {
      // Validate
      if (!paymentFormData.invoice_id) {
        toast.error('Please select an invoice');
        return;
      }
      
      const paymentAmount = parseFloat(paymentFormData.amount);
      if (isNaN(paymentAmount) || paymentAmount <= 0) {
        toast.error('Please enter a valid payment amount');
        return;
      }

      const selectedInvoice = tenantInvoices.find(inv => inv.id === parseInt(paymentFormData.invoice_id));
      if (!selectedInvoice) {
        toast.error('Selected invoice not found');
        return;
      }

      // Use backend-calculated remaining balance if available
      const remainingBalance = selectedInvoice.remaining_balance !== undefined 
        ? selectedInvoice.remaining_balance 
        : (selectedInvoice.total_with_penalty || selectedInvoice.amount) - (selectedInvoice.amount_paid || 0);
      
      // Allow overpayment (advance/upfront payment) with confirmation
      if (paymentAmount > remainingBalance) {
        const overpaymentAmount = paymentAmount - remainingBalance;
        const confirmed = window.confirm(
          `This payment (KES ${paymentAmount.toLocaleString()}) exceeds the invoice balance (KES ${remainingBalance.toLocaleString()}) by KES ${overpaymentAmount.toLocaleString()}.\n\n` +
          `The excess amount will be applied as a credit to the tenant's account for future invoices.\n\n` +
          `Do you want to continue?`
        );
        if (!confirmed) return;
      }

      // Prepare payload matching backend schema
      const payload = {
        invoice_id: parseInt(paymentFormData.invoice_id),
        amount: paymentAmount,
        payment_method: paymentFormData.payment_method,
        payment_date: paymentFormData.payment_date,
        transaction_reference: paymentFormData.transaction_ref || `MANUAL-${Date.now()}`,
      };
      
      console.log('Submitting payment:', payload);
      
      // Submit payment
      const response = await paymentService.submitManualPayment(payload);
      
      console.log('Payment response:', response.data);
      
      // Show success message with credit information if applicable
      const data = response.data;
      if (data.credit_added > 0) {
        toast.success(
          `✅ Payment recorded! KES ${data.credit_added.toLocaleString()} added to tenant's credit balance. New credit balance: KES ${data.current_credit_balance.toLocaleString()}`,
          { autoClose: 8000 }
        );
      } else {
        toast.success('Payment recorded successfully');
      }
      
      handleClosePaymentDialog();
      fetchTenants();
    } catch (error) {
      console.error('Payment submission error:', error);
      console.error('Error response:', error.response);
      
      let errorMessage = 'Failed to record payment';
      
      if (error.response) {
        const responseData = error.response.data;
        
        if (typeof responseData === 'object') {
          if (responseData.detail) {
            const detail = Array.isArray(responseData.detail) 
              ? responseData.detail.map(e => e.msg || e).join(', ')
              : responseData.detail;
            errorMessage = detail;
          } else if (responseData.message) {
            errorMessage = responseData.message;
          } else {
            const validationErrors = Object.entries(responseData)
              .filter(([key]) => key !== 'status')
              .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
              .join('; ');
            errorMessage = validationErrors || `Server error: ${error.response.status}`;
          }
        } else {
          errorMessage = `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check if the backend is running.';
      } else {
        errorMessage = error.message || 'An error occurred while recording payment';
      }
      
      toast.error(errorMessage);
    }
  };

  // ---- Report Generation Handlers ----
  const handleOpenReportDialog = (tenant) => {
    setSelectedTenantForReport(tenant);
    setReportFilters({
      period: 'monthly',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      start_date: '',
      end_date: '',
    });
    setOpenReportDialog(true);
  };

  const handleCloseReportDialog = () => {
    setOpenReportDialog(false);
    setSelectedTenantForReport(null);
  };

  const handleReportFilterChange = (e) => {
    setReportFilters({
      ...reportFilters,
      [e.target.name]: e.target.value,
    });
  };

  const handleGenerateTenantReport = async () => {
    if (!selectedTenantForReport) return;

    try {
      setReportLoading(true);

      // Build params based on period type
      const params = { period: reportFilters.period };
      if (reportFilters.period === 'monthly') {
        params.month = reportFilters.month;
        params.year = reportFilters.year;
      } else if (reportFilters.period === 'yearly') {
        params.year = reportFilters.year;
      } else if (reportFilters.period === 'custom') {
        if (!reportFilters.start_date || !reportFilters.end_date) {
          toast.error('Please select both start and end dates');
          setReportLoading(false);
          return;
        }
        params.start_date = reportFilters.start_date;
        params.end_date = reportFilters.end_date;
      }

      const response = await reportService.getTenantPaymentReport(selectedTenantForReport.id, params);
      const data = response.data;

      // Generate PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();

      // Title
      doc.setFontSize(20);
      doc.setTextColor(33, 150, 243);
      doc.text('TENANT PAYMENT REPORT', pageWidth / 2, 20, { align: 'center' });

      // Tenant info
      doc.setFontSize(11);
      doc.setTextColor(51, 51, 51);
      doc.text(`Tenant: ${data.tenant.name}`, 14, 35);
      doc.text(`Room: ${data.tenant.room_number} - ${data.tenant.property_name}`, 14, 42);
      doc.text(`Phone: ${data.tenant.phone}`, 14, 49);
      doc.text(`Email: ${data.tenant.email}`, 14, 56);

      // Period
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Period: ${data.period}`, 14, 66);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 73);

      // Summary
      doc.setFontSize(14);
      doc.setTextColor(33, 150, 243);
      doc.text('Summary', 14, 85);

      const summaryRows = [
        ['Total Payments (count)', data.summary.payment_count.toString()],
        ['Total Cash Received', `KES ${data.summary.total_paid.toLocaleString()}`],
      ];

      // Add method breakdown
      if (data.summary.method_breakdown) {
        Object.entries(data.summary.method_breakdown).forEach(([method, amount]) => {
          summaryRows.push([`${method.toUpperCase()} Received`, `KES ${amount.toLocaleString()}`]);
        });
      }

      // Show credit applied separately
      if (data.summary.total_credit_applied > 0) {
        summaryRows.push(['Credit Applied (from overpayment)', `KES ${data.summary.total_credit_applied.toLocaleString()}`]);
      }

      summaryRows.push(['Total Cumulative Unpaid', `KES ${data.summary.total_unpaid.toLocaleString()}`]);

      autoTable(doc, {
        startY: 90,
        head: [['Metric', 'Value']],
        body: summaryRows,
        theme: 'grid',
        headStyles: { fillColor: [33, 150, 243] },
        margin: { left: 14, right: 14 },
      });

      let yPos = doc.lastAutoTable.finalY + 10;

      // Security Deposit section
      doc.setFontSize(14);
      doc.setTextColor(33, 150, 243);
      doc.text('Security Deposit', 14, yPos);
      yPos += 5;
      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Amount']],
        body: [
          ['Deposit Paid', `KES ${(data.security_deposit?.paid || 0).toLocaleString()}`],
          ['Used / Applied', `KES ${(data.security_deposit?.used || 0).toLocaleString()}`],
          ['Deductions', `KES ${(data.security_deposit?.deductions || 0).toLocaleString()}`],
          ['Remaining', `KES ${(data.security_deposit?.remaining || 0).toLocaleString()}`],
        ],
        theme: 'grid',
        headStyles: { fillColor: [76, 175, 80] },
        margin: { left: 14, right: 14 },
      });
      yPos = doc.lastAutoTable.finalY + 10;

      // Payments table
      if (data.payments && data.payments.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(33, 150, 243);
        doc.text('Payment Details', 14, yPos);
        yPos += 5;

        const paymentRows = data.payments.map((p) => [
          new Date(p.payment_date).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
          }),
          p.invoice_number,
          p.description || 'Rent',
          `KES ${p.amount.toLocaleString()}`,
          p.payment_method.toUpperCase(),
          p.transaction_reference,
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Date', 'Invoice #', 'Description', 'Amount', 'Method', 'Reference']],
          body: paymentRows,
          theme: 'striped',
          headStyles: { fillColor: [33, 150, 243] },
          margin: { left: 14, right: 14 },
          styles: { fontSize: 9 },
        });
        yPos = doc.lastAutoTable.finalY + 10;
      } else {
        doc.setFontSize(11);
        doc.setTextColor(150, 150, 150);
        doc.text('No payments found for the selected period.', pageWidth / 2, yPos + 10, { align: 'center' });
        yPos += 20;
      }

      // Unpaid invoices table
      if (data.unpaid_invoices && data.unpaid_invoices.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(244, 67, 54);
        doc.text('Unpaid/Outstanding Invoices', 14, yPos);
        yPos += 5;

        const unpaidRows = data.unpaid_invoices.map((i) => [
          new Date(i.due_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
          i.invoice_number,
          i.description || 'Rent',
          `KES ${i.amount.toLocaleString()}`,
          `KES ${(i.penalty_amount || 0).toLocaleString()}`,
          (i.waiver_amount || 0) > 0 ? `-KES ${i.waiver_amount.toLocaleString()}` : 'KES 0',
          `KES ${(i.amount_paid || 0).toLocaleString()}`,
          `KES ${i.remaining_balance.toLocaleString()}`,
          i.status.toUpperCase(),
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Due Date', 'Invoice #', 'Description', 'Amount', 'Penalty', 'Waiver', 'Paid', 'Remaining', 'Status']],
          body: unpaidRows,
          theme: 'grid',
          headStyles: { fillColor: [244, 67, 54] },
          margin: { left: 14, right: 14 },
          styles: { fontSize: 9 },
        });
        yPos = doc.lastAutoTable.finalY + 5;
        doc.setFontSize(11);
        doc.setTextColor(244, 67, 54);
        doc.text(`Total Outstanding: KES ${data.summary.total_unpaid.toLocaleString()}`, 14, yPos);
        yPos += 8;
      }

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          'This is a computer-generated report. Property Management System.',
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      const safeName = data.tenant.name.replace(/[^a-zA-Z0-9]/g, '_');
      doc.save(`tenant_report_${safeName}_${data.period.replace(/\s+/g, '_')}.pdf`);
      toast.success('Report generated and downloaded successfully!');
      handleCloseReportDialog();
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  const monthOptions = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const handleOpenPasswordDialog = (tenant) => {
    setSelectedTenantForReset(tenant);
    setNewPassword('');
    setConfirmPassword('');
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setSelectedTenantForReset(null);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handlePasswordReset = async () => {
    // Validation
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      await tenantService.resetPassword(selectedTenantForReset.id, newPassword);
      toast.success(`Password reset successfully for ${selectedTenantForReset.full_name}`);
      handleClosePasswordDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
      console.error(error);
    }
  };

  const handleCopyPassword = () => {
    if (tempPasswordInfo) {
      navigator.clipboard.writeText(tempPasswordInfo.password);
      toast.success('Password copied to clipboard!');
    }
  };

  return (
    <Box>
      <PageHeader
        title="Tenants"
        subtitle="Manage your property tenants"
        breadcrumbs={[
          { label: 'Tenants' }
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Tenant
          </Button>
        }
      />

      {loading ? (
        <Loading message="Loading tenants..." />
      ) : tenants.length === 0 ? (
        <EmptyState
          title="No tenants yet"
          description="Get started by adding your first tenant to the system."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Your First Tenant
            </Button>
          }
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50"></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Contact</strong></TableCell>
                <TableCell><strong>Room/Unit</strong></TableCell>
                <TableCell align="center"><strong>First Deposit</strong></TableCell>
                <TableCell align="center"><strong>Credit Balance</strong></TableCell>
                <TableCell align="center"><strong>Total Outstanding</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((tenant) => {
                const financial = tenant.financial_summary || {};
                const hasOutstanding = financial.total_outstanding > 0;
                const hasOverdue = financial.due_invoices > 0;
                
                return (
                  <React.Fragment key={tenant.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleExpandRow(tenant.id)}
                        >
                          {expandedRows[tenant.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {tenant.full_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{tenant.email}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tenant.phone}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {tenant.room ? (
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <RoomIcon fontSize="small" color="action" />
                              <Typography variant="body2" fontWeight="600">
                                {tenant.room.room_number}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {tenant.room.property_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {tenant.room.room_type} • {formatCurrency(tenant.room.monthly_rent)}/mo
                            </Typography>
                          </Box>
                        ) : (
                          <Chip
                            label="No Room Assigned"
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={`Deposited on ${formatDate(financial.first_deposit?.date)}`}>
                          <Chip
                            icon={<DepositIcon />}
                            label={formatCurrency(financial.first_deposit?.amount)}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {financial.credit_balance > 0 ? (
                          <Tooltip title="Available credit for future invoices">
                            <Chip
                              label={formatCurrency(financial.credit_balance)}
                              color="success"
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="No credit balance">
                            <Chip
                              label={formatCurrency(0)}
                              variant="outlined"
                              size="small"
                              color="default"
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {hasOutstanding ? (
                          <Tooltip title={`${financial.unpaid_invoices} unpaid, ${financial.partial_paid_invoices} partial`}>
                            <Chip
                              icon={<WarningIcon />}
                              label={formatCurrency(financial.total_outstanding)}
                              color={hasOverdue ? 'error' : 'warning'}
                              size="small"
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            icon={<CheckIcon />}
                            label="All Paid"
                            color="success"
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={tenant.is_active ? 'Active' : 'Inactive'}
                          color={tenant.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Reset Password">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleOpenPasswordDialog(tenant)}
                          >
                            <PasswordIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Tenant">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(tenant)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Tenant">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(tenant.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded Row Details */}
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 0, backgroundColor: '#f9fafb' }}>
                        <Collapse in={expandedRows[tenant.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 3, px: 2 }}>
                            {/* Room Details */}
                            {tenant.room && (
                              <Box sx={{ mb: 3, p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                  <RoomIcon color="primary" />
                                  <Typography variant="h6">Room Details</Typography>
                                </Stack>
                                <Grid container spacing={2}>
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Room Number
                                    </Typography>
                                    <Typography variant="body1" fontWeight="600">
                                      {tenant.room.room_number}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Property
                                    </Typography>
                                    <Typography variant="body1">
                                      {tenant.room.property_name}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Type
                                    </Typography>
                                    <Typography variant="body1">
                                      {tenant.room.room_type}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Monthly Rent
                                    </Typography>
                                    <Typography variant="body1" fontWeight="600" color="primary">
                                      {formatCurrency(tenant.room.monthly_rent)}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                            )}
                            
                            <Typography variant="h6" gutterBottom sx={{ mb: 3, pb: 1, borderBottom: '2px solid #1976d2' }}>
                              Financial Summary
                            </Typography>
                            
                            {/* Credit Balance & Overpayment Info */}
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" fontWeight="600" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                💰 Credit & Overpayment Summary
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                  <Card variant="outlined" sx={{ 
                                    height: '100%',
                                    bgcolor: (financial.total_credit_generated || 0) > 0 ? '#e8f5e9' : '#f5f5f5',
                                    borderColor: (financial.total_credit_generated || 0) > 0 ? 'success.main' : 'divider'
                                  }}>
                                    <CardContent>
                                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight="600">
                                        Total Overpayment Credit
                                      </Typography>
                                      <Typography variant="h5" color={(financial.total_credit_generated || 0) > 0 ? 'success.main' : 'text.secondary'} fontWeight="bold" sx={{ mt: 1 }}>
                                        {formatCurrency(financial.total_credit_generated || 0)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                        From M-Pesa overpayments
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Card variant="outlined" sx={{ 
                                    height: '100%',
                                    bgcolor: (financial.total_credit_used || 0) > 0 ? '#e3f2fd' : '#f5f5f5',
                                    borderColor: (financial.total_credit_used || 0) > 0 ? 'info.main' : 'divider'
                                  }}>
                                    <CardContent>
                                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight="600">
                                        Credit Auto-Applied
                                      </Typography>
                                      <Typography variant="h5" color={(financial.total_credit_used || 0) > 0 ? 'info.main' : 'text.secondary'} fontWeight="bold" sx={{ mt: 1 }}>
                                        {formatCurrency(financial.total_credit_used || 0)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                        Applied to invoices automatically
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Card variant="outlined" sx={{ 
                                    height: '100%',
                                    bgcolor: financial.credit_balance > 0 ? '#e8f5e9' : '#f5f5f5',
                                    border: financial.credit_balance > 0 ? '2px solid #4caf50' : '1px solid #e0e0e0'
                                  }}>
                                    <CardContent>
                                      <Typography variant="caption" color={financial.credit_balance > 0 ? 'success.dark' : 'text.secondary'} textTransform="uppercase" fontWeight="600">
                                        Remaining Credit
                                      </Typography>
                                      <Typography variant="h5" color={financial.credit_balance > 0 ? 'success.main' : 'text.secondary'} fontWeight="bold" sx={{ mt: 1 }}>
                                        {formatCurrency(financial.credit_balance || 0)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                        {financial.credit_balance > 0 ? 'Available for future invoices' : 'All credits applied'}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              </Grid>
                            </Box>
                            
                            {/* Security Deposit Section */}
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" fontWeight="600" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DepositIcon /> Security Deposit
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                  <Card variant="outlined" sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
                                    <CardContent>
                                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight="600">
                                        Deposit Paid
                                      </Typography>
                                      <Typography variant="h5" color="primary" fontWeight="bold" sx={{ mt: 1 }}>
                                        {formatCurrency(financial.security_deposit_paid || 0)}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Card variant="outlined" sx={{ bgcolor: '#fff3e0', height: '100%' }}>
                                    <CardContent>
                                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight="600">
                                        Amount Used
                                      </Typography>
                                      <Typography variant="h5" color="warning.main" fontWeight="bold" sx={{ mt: 1 }}>
                                        {formatCurrency(financial.security_deposit_used || 0)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                        (Rent/Damages)
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Card variant="outlined" sx={{ bgcolor: '#e8f5e9', borderColor: 'success.main', borderWidth: 2, height: '100%' }}>
                                    <CardContent>
                                      <Typography variant="caption" color="success.dark" textTransform="uppercase" fontWeight="700">
                                        Remaining
                                      </Typography>
                                      <Typography variant="h5" color="success.main" fontWeight="bold" sx={{ mt: 1 }}>
                                        {formatCurrency(financial.security_deposit_remaining || 0)}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              </Grid>
                            </Box>
                            
                            {/* Invoice Status Section */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle1" fontWeight="600" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ReceiptIcon /> Invoice Status
                              </Typography>
                              <Grid container spacing={2}>
                                {/* Row 1: Invoice Counts */}
                                <Grid item xs={6} sm={3}>
                                  <Card variant="outlined" sx={{ height: '100%', bgcolor: '#fafafa' }}>
                                    <CardContent>
                                      <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                                        Total
                                      </Typography>
                                      <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                                        {financial.total_invoices || 0}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                
                                <Grid item xs={6} sm={3}>
                                  <Card variant="outlined" sx={{ height: '100%', bgcolor: financial.due_invoices > 0 ? '#ffebee' : '#fafafa', borderColor: financial.due_invoices > 0 ? 'error.main' : 'divider' }}>
                                    <CardContent>
                                      <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                                        Overdue
                                      </Typography>
                                      <Typography variant="h4" fontWeight="bold" color={financial.due_invoices > 0 ? 'error.main' : 'text.primary'} sx={{ mt: 1 }}>
                                        {financial.due_invoices || 0}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                
                                <Grid item xs={6} sm={3}>
                                  <Card variant="outlined" sx={{ height: '100%', bgcolor: financial.unpaid_invoices > 0 ? '#fff3e0' : '#fafafa', borderColor: financial.unpaid_invoices > 0 ? 'warning.main' : 'divider' }}>
                                    <CardContent>
                                      <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                                        Unpaid
                                      </Typography>
                                      <Typography variant="h4" fontWeight="bold" color={financial.unpaid_invoices > 0 ? 'warning.main' : 'text.primary'} sx={{ mt: 1 }}>
                                        {financial.unpaid_invoices || 0}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                
                                <Grid item xs={6} sm={3}>
                                  <Card variant="outlined" sx={{ height: '100%', bgcolor: financial.partial_paid_invoices > 0 ? '#e3f2fd' : '#fafafa' }}>
                                    <CardContent>
                                      <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                                        Partial
                                      </Typography>
                                      <Typography variant="h4" fontWeight="bold" color="info.main" sx={{ mt: 1 }}>
                                        {financial.partial_paid_invoices || 0}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                
                                {/* Row 2: Financial Amounts */}
                                <Grid item xs={12} sm={4}>
                                  <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent>
                                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight="600">
                                        Unpaid Amount
                                      </Typography>
                                      <Typography variant="h5" fontWeight="bold" color="warning.main" sx={{ mt: 1 }}>
                                        {formatCurrency(financial.unpaid_amount)}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                
                                <Grid item xs={12} sm={4}>
                                  <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent>
                                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight="600">
                                        Partial Paid
                                      </Typography>
                                      <Typography variant="h5" fontWeight="bold" color="info.main" sx={{ mt: 1 }}>
                                        {formatCurrency(financial.partial_paid_amount_paid || 0)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                        Balance: {formatCurrency(financial.partial_paid_remaining || 0)}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                
                                <Grid item xs={12} sm={4}>
                                  <Card 
                                    sx={{ 
                                      height: '100%',
                                      bgcolor: financial.total_outstanding > 0 ? '#ffebee' : '#e8f5e9',
                                      border: 2,
                                      borderColor: financial.total_outstanding > 0 ? 'error.main' : 'success.main',
                                      boxShadow: 2
                                    }}
                                  >
                                    <CardContent>
                                      <Typography variant="caption" color={financial.total_outstanding > 0 ? 'error.dark' : 'success.dark'} textTransform="uppercase" fontWeight="700">
                                        TOTAL OUTSTANDING
                                      </Typography>
                                      <Typography variant="h4" fontWeight="bold" color={financial.total_outstanding > 0 ? 'error.main' : 'success.main'} sx={{ mt: 1 }}>
                                        {formatCurrency(financial.total_outstanding)}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              </Grid>
                            </Box>
                            
                            {/* Action Buttons: Receive Payment + Generate Report */}
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                              {financial.total_outstanding > 0 && (
                                <Button
                                  variant="contained"
                                  color="success"
                                  startIcon={<PaymentIcon />}
                                  onClick={() => handleOpenPaymentDialog(tenant)}
                                  size="large"
                                >
                                  Receive Payment
                                </Button>
                              )}
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<ReportIcon />}
                                onClick={() => handleOpenReportDialog(tenant)}
                                size="large"
                              >
                                Generate Report
                              </Button>
                            </Box>
                            
                            {tenant.move_in_date && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Move-in Date: {formatDate(tenant.move_in_date)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTenant ? 'Edit Tenant' : 'Add New Tenant'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Full Name"
            name="full_name"
            value={formData.full_name}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="National ID"
            name="national_id"
            value={formData.national_id}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          
          {/* Opening Balance Section */}
          {!editingTenant && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Opening Balance (Optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                For existing tenants being migrated to the system. Leave as 0 for new tenants.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Balance Type"
                    name="balance_type"
                    value={formData.balance_type}
                    onChange={handleInputChange}
                    helperText="Credit = Advance payment, Debit = Arrears/Owed"
                  >
                    <MenuItem value="credit">Credit (Advance Payment)</MenuItem>
                    <MenuItem value="debit">Debit (Arrears/Amount Owed)</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Opening Balance Amount (KES)"
                    name="opening_balance"
                    type="number"
                    value={formData.opening_balance}
                    onChange={handleInputChange}
                    inputProps={{ min: 0, step: 100 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Balance Notes"
                    name="balance_notes"
                    value={formData.balance_notes}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                    placeholder="e.g., Balance brought forward from old system"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTenant ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive Payment Dialog */}
      <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <PaymentIcon color="success" />
            <Typography variant="h6">Receive Payment</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedTenantForPayment && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tenant: <strong>{selectedTenantForPayment.full_name}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Room: <strong>{selectedTenantForPayment.room?.room_number || 'N/A'}</strong>
              </Typography>
              
              {tenantInvoices.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No unpaid invoices found for this tenant.
                </Alert>
              ) : (
                <>
                  <TextField
                    fullWidth
                    select
                    label="Select Invoice"
                    name="invoice_id"
                    value={paymentFormData.invoice_id}
                    onChange={handlePaymentInputChange}
                    margin="normal"
                    required
                  >
                    {tenantInvoices.map((invoice) => {
                      // Use backend-calculated remaining_balance if available
                      const balance = invoice.remaining_balance !== undefined 
                        ? invoice.remaining_balance 
                        : (invoice.total_with_penalty || invoice.amount) - (invoice.amount_paid || 0);
                      
                      // Format description
                      let description = invoice.description || 'Rent';
                      if (invoice.billing_month && invoice.billing_year) {
                        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                                          'July', 'August', 'September', 'October', 'November', 'December'];
                        description = `Rent for ${monthNames[invoice.billing_month - 1]} ${invoice.billing_year}`;
                      }
                      
                      return (
                        <MenuItem key={invoice.id} value={invoice.id}>
                          Invoice #{invoice.invoice_number || invoice.id} - {description} - Balance: KES {balance.toLocaleString()}
                          {invoice.penalty_amount > 0 && ` (Penalty: KES ${invoice.penalty_amount.toLocaleString()})`}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {paymentFormData.invoice_id && (() => {
                    const selectedInvoice = tenantInvoices.find(inv => inv.id === parseInt(paymentFormData.invoice_id));
                    if (selectedInvoice) {
                      // Use backend-calculated remaining_balance
                      const balance = selectedInvoice.remaining_balance !== undefined 
                        ? selectedInvoice.remaining_balance 
                        : (selectedInvoice.total_with_penalty || selectedInvoice.amount) - (selectedInvoice.amount_paid || 0);
                      
                      // Format description
                      let description = selectedInvoice.description || 'Rent Invoice';
                      if (selectedInvoice.billing_month && selectedInvoice.billing_year) {
                        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                                          'July', 'August', 'September', 'October', 'November', 'December'];
                        description = `${monthNames[selectedInvoice.billing_month - 1]} ${selectedInvoice.billing_year}`;
                      }
                      
                      return (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Invoice:</strong> #{selectedInvoice.invoice_number} ({description})
                          </Typography>
                          <Typography variant="body2">
                            <strong>Original Amount:</strong> KES {selectedInvoice.amount.toLocaleString()}
                          </Typography>
                          {selectedInvoice.penalty_amount > 0 && (
                            <Typography variant="body2" color="error">
                              <strong>Late Payment Penalty:</strong> KES {selectedInvoice.penalty_amount.toLocaleString()}
                            </Typography>
                          )}
                          {selectedInvoice.penalty_amount > 0 && (
                            <Typography variant="body2" fontWeight="600">
                              <strong>Total Amount Due:</strong> KES {(selectedInvoice.amount + selectedInvoice.penalty_amount).toLocaleString()}
                            </Typography>
                          )}
                          {(selectedInvoice.amount_paid || 0) > 0 && (
                            <Typography variant="body2" color="success.main">
                              <strong>Amount Paid:</strong> KES {(selectedInvoice.amount_paid || 0).toLocaleString()}
                            </Typography>
                          )}
                          <Typography variant="body2" fontWeight="bold" color="warning.main" sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                            <strong>Remaining Balance:</strong> KES {balance.toLocaleString()}
                          </Typography>
                        </Alert>
                      );
                    }
                    return null;
                  })()}

                  <TextField
                    fullWidth
                    label="Payment Amount (KES)"
                    name="amount"
                    type="number"
                    value={paymentFormData.amount}
                    onChange={handlePaymentInputChange}
                    margin="normal"
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Enter the amount being paid. Can be partial or full payment."
                  />

                  <TextField
                    fullWidth
                    label="Payment Date"
                    name="payment_date"
                    type="date"
                    value={paymentFormData.payment_date}
                    onChange={handlePaymentInputChange}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    required
                  />

                  <TextField
                    fullWidth
                    select
                    label="Payment Method"
                    name="payment_method"
                    value={paymentFormData.payment_method}
                    onChange={handlePaymentInputChange}
                    margin="normal"
                    required
                  >
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="mpesa">M-Pesa</MenuItem>
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="cheque">Cheque</MenuItem>
                  </TextField>

                  <TextField
                    fullWidth
                    label="Transaction Reference (Optional)"
                    name="transaction_ref"
                    value={paymentFormData.transaction_ref}
                    onChange={handlePaymentInputChange}
                    margin="normal"
                    placeholder="e.g., M-Pesa code, cheque number"
                  />
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmitPayment} 
            variant="contained" 
            color="success"
            disabled={tenantInvoices.length === 0 || !paymentFormData.invoice_id}
          >
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Report Dialog */}
      <Dialog open={openReportDialog} onClose={handleCloseReportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <ReportIcon color="primary" />
            <Typography variant="h6">Generate Tenant Payment Report</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedTenantForReport && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Tenant: <strong>{selectedTenantForReport.full_name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Room: <strong>{selectedTenantForReport.room?.room_number || 'N/A'}</strong> - {selectedTenantForReport.room?.property_name || ''}
                </Typography>
              </Box>

              <TextField
                fullWidth
                select
                label="Report Period"
                name="period"
                value={reportFilters.period}
                onChange={handleReportFilterChange}
                margin="normal"
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
                <MenuItem value="custom">Custom Date Range</MenuItem>
              </TextField>

              {reportFilters.period === 'monthly' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      select
                      label="Month"
                      name="month"
                      value={reportFilters.month}
                      onChange={handleReportFilterChange}
                    >
                      {monthOptions.map((m) => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      select
                      label="Year"
                      name="year"
                      value={reportFilters.year}
                      onChange={handleReportFilterChange}
                    >
                      {yearOptions.map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              )}

              {reportFilters.period === 'yearly' && (
                <TextField
                  fullWidth
                  select
                  label="Year"
                  name="year"
                  value={reportFilters.year}
                  onChange={handleReportFilterChange}
                  margin="normal"
                >
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </TextField>
              )}

              {reportFilters.period === 'custom' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      name="start_date"
                      type="date"
                      value={reportFilters.start_date}
                      onChange={handleReportFilterChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="End Date"
                      name="end_date"
                      type="date"
                      value={reportFilters.end_date}
                      onChange={handleReportFilterChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReportDialog}>Cancel</Button>
          <Button
            onClick={handleGenerateTenantReport}
            variant="contained"
            color="primary"
            startIcon={<PdfIcon />}
            disabled={reportLoading}
          >
            {reportLoading ? 'Generating...' : 'Generate PDF Report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <PasswordIcon color="warning" />
            <Typography variant="h6">Reset Password</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedTenantForReset && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Resetting password for:
              </Typography>
              <Typography variant="h6">
                {selectedTenantForReset.full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedTenantForReset.email}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            required
            helperText="Password must be at least 6 characters long"
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            error={confirmPassword && newPassword !== confirmPassword}
            helperText={
              confirmPassword && newPassword !== confirmPassword
                ? 'Passwords do not match'
                : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>Cancel</Button>
          <Button 
            onClick={handlePasswordReset} 
            variant="contained" 
            color="warning"
            disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Temporary Password Display Dialog */}
      <Dialog 
        open={openTempPasswordDialog} 
        onClose={() => setOpenTempPasswordDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckIcon color="success" />
            <Typography variant="h6">Tenant Created Successfully!</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {tempPasswordInfo && (
            <Box>
              <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                A user account has been created for:
              </Typography>
              
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>
                  {tempPasswordInfo.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Email: {tempPasswordInfo.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Username: {tempPasswordInfo.username}
                </Typography>
              </Paper>

              <Paper 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  backgroundColor: '#fff3e0',
                  border: '2px solid #ff9800'
                }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WarningIcon color="warning" />
                    <Typography variant="subtitle1" fontWeight="600">
                      Temporary Password
                    </Typography>
                  </Stack>
                  
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      fullWidth
                      value={tempPasswordInfo.password}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <IconButton onClick={handleCopyPassword} edge="end">
                            <CopyIcon />
                          </IconButton>
                        ),
                        sx: {
                          fontFamily: 'monospace',
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          backgroundColor: 'white'
                        }
                      }}
                    />
                  </Stack>
                </Stack>
              </Paper>

              <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', border: '1px solid #2196f3' }}>
                <Typography variant="body2" color="primary" fontWeight="600" gutterBottom>
                  ⚠️ IMPORTANT:
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    <li>This password will NOT be shown again</li>
                    <li>Please copy and share it with the tenant securely</li>
                    <li>The tenant can log in with their email and this password</li>
                    <li>Advise the tenant to change this password after first login</li>
                  </ul>
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCopyPassword} 
            startIcon={<CopyIcon />}
            variant="outlined"
          >
            Copy Password
          </Button>
          <Button 
            onClick={() => setOpenTempPasswordDialog(false)} 
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tenants;
